<template>
  <v-row class="mt-12">
    <v-col cols="12">
      <Subtitle text="Termin" :font-size="1.2" :font-weight="900" />
    </v-col>
    <v-col id="event-form__start_date" class="pb-0" cols="12" sm="6">
      <DatePicker :label="t$('.start_date')" v-model="startDate" />
    </v-col>
    <v-col class="pb-0" cols="12" sm="6">
      <DatePicker
        :label="t$('.end_date')"
        :is-mandatory="false"
        v-model="endDate"
        :minDate="startDate"
        :picker-date="endDatePickerDate"
      />
    </v-col>
    <v-col cols="12" class="py-0">
      <v-switch v-model="allDay" inset class="mt-0">
        <template v-slot:label>
          <TextBody :text="t$('.all_day_event')" large />
        </template>
      </v-switch>
    </v-col>
    <v-col id="event-form__start_time" cols="12" sm="6">
      <TimePicker
        :label="t$('.start_time')"
        v-if="!allDay"
        :isMandatory="!allDay"
        v-model="startTime"
        :rules="[validTimeFormat]"
      />
    </v-col>
    <v-col id="event-form__end_time" cols="12" sm="6">
      <TimePicker
        :label="t$('.end_time')"
        v-if="!allDay"
        :isMandatory="false"
        v-model="endTime"
        :min-time="minTime"
        :rules="[validTimeFormat]"
      />
      <div style="display: flex" v-if="!allDay">
        <v-icon style="margin-right: 9px" color="#6c757d">{{
          infoIcon
        }}</v-icon>
      </div>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import {
  computed,
  ComputedRef,
  defineComponent,
  inject,
  PropType,
  WritableComputedRef,
} from "vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import EventFormService from "features/events/form/event-form.service"
import DatePicker from "components/UI/DatePicker.vue"
import TextBody from "components/UI/TextBody.vue"
import TimePicker from "components/UI/TimePicker.vue"
import Subtitle from "components/UI/Subtitle.vue"
import { PanelIdentifier } from "features/events/form/event-form.types"
import { EventForm } from "components/EventForm/EventForm.vue"
import { Rule } from "features/common/forms/form.service"
import { DateUtil } from "app/util/date.helper"
import { mdiInformation, mdiInformationOutline } from "@mdi/js"

export default defineComponent({
  name: "DateAndTimeFields",
  components: { Subtitle, TextBody, DatePicker, TimePicker },
  props: {
    panelIdentifier: {
      type: String as PropType<PanelIdentifier>,
      required: true,
    },
  },
  setup(props) {
    const { t$ } = useTranslate("components.event_form")
    const eventFormService: EventFormService<EventForm> =
      inject<EventFormService<EventForm>>("EventFormService")

    eventFormService.registerProperty({
      name: "start_date",
    })

    eventFormService.registerProperty({
      name: "start_time",
    })

    eventFormService.registerProperty({
      name: "end_date",
    })

    eventFormService.registerProperty({
      name: "end_time",
    })

    eventFormService.registerProperty({
      name: "all_day",
    })

    const startTimeRule: Rule<EventForm, "start_time"> = {
      field: "start_time",
      message: t$(".please_enter_start_time"),
      selector: "event-form__start_time",
      panelIdentifier: props.panelIdentifier,
      action: (v) => !!v || eventFormService.getFormValue("all_day").value,
    }

    const startDateRule: Rule<EventForm, "start_date"> = {
      field: "start_date",
      message: t$(".validate_start_date"),
      selector: "event-form__start_date",
      panelIdentifier: props.panelIdentifier,
      action: (v) => !!v && !!v.length,
    }

    eventFormService.registerRule(startTimeRule as Rule<EventForm>)
    eventFormService.registerRule(startDateRule as Rule<EventForm>)

    const handleDateOrTimeChange = (previousValues: {
      startDate?: string
      startTime?: string
    }) => {
      const currentStartDate = eventFormService.getFormValue("start_date").value
      const currentEndDate = eventFormService.getFormValue("end_date").value
      const currentStartTime = eventFormService.getFormValue("start_time").value
      const currentEndTime = eventFormService.getFormValue("end_time").value

      // Case 1: End date is before start date
      if (
        currentStartDate &&
        currentEndDate &&
        DateUtil.isBeforeStr(currentEndDate, currentStartDate)
      ) {
        // If previous start date is available, move end date by the difference
        if (previousValues.startDate) {
          const previousTimeDiff = DateUtil.getDiff(
            new Date(previousValues.startDate),
            new Date(currentEndDate)
          )
          eventFormService.setFormValue(
            "end_date",
            DateUtil.getDateString(
              DateUtil.add(new Date(currentStartDate), previousTimeDiff)
            )
          )
        }
        // Else set end date to start date
        else {
          eventFormService.setFormValue(
            "end_date",
            DateUtil.getDateString(new Date(currentStartDate))
          )
        }
      }

      // Case 2: End Time is before start time and on the same day
      if (
        currentStartDate === currentEndDate &&
        currentStartTime &&
        currentEndTime &&
        DateUtil.toMinutes(currentEndTime) <
          DateUtil.toMinutes(currentStartTime)
      ) {
        eventFormService.setFormValue("end_time", null)
      }
    }

    const startDate: WritableComputedRef<string> = computed({
      get(): string {
        return eventFormService.getFormValue("start_date").value ?? null
      },
      set(newStartDate: string): void {
        const previousStartDate =
          eventFormService.getFormValue("start_date").value
        eventFormService.setFormValue("start_date", newStartDate)
        handleDateOrTimeChange({ startDate: previousStartDate })
        eventFormService.validate()
      },
    })

    const endDate: WritableComputedRef<string> = computed({
      get(): string {
        return eventFormService.getFormValue("end_date").value ?? null
      },
      set(value: string): void {
        eventFormService.setFormValue("end_date", value)
        handleDateOrTimeChange({})
      },
    })

    const endDatePickerDate: ComputedRef<string> = computed(() => {
      return endDate.value || startDate.value
    })

    const allDay: WritableComputedRef<boolean> = computed({
      get(): boolean {
        const allDay = eventFormService.getFormValue("all_day").value
        if (allDay !== undefined) {
          return allDay
        } else {
          eventFormService.setFormValue("all_day", true)
          return true
        }
      },
      set(value: boolean): void {
        if (value) {
          eventFormService.setFormValue("start_time", null)
          eventFormService.setFormValue("end_time", null)
        }
        eventFormService.setFormValue("all_day", value)
      },
    })

    const startTime: WritableComputedRef<string> = computed({
      get(): string {
        const start_time = eventFormService.getFormValue("start_time").value
        return start_time && start_time !== "00:00" ? start_time : null
      },
      set(value: string): void {
        eventFormService.setFormValue("start_time", value)
        handleDateOrTimeChange({})
        eventFormService.validate()
      },
    })

    const endTime: WritableComputedRef<string> = computed({
      get(): string {
        const end_time = eventFormService.getFormValue("end_time").value
        if (
          end_time === "23:59" ||
          end_time === "23:59:59" ||
          end_time === "00:00"
        ) {
          eventFormService.setFormValue("end_time", null)
          return null
        }
        return end_time || null
      },
      set(value: string): void {
        if (!endDate.value && startDate.value) {
          endDate.value = startDate.value
        }
        if (value === "23:59" || value === "23:59:59" || value === "00:00") {
          eventFormService.setFormValue("end_time", null)
        } else {
          eventFormService.setFormValue("end_time", value)
        }
        handleDateOrTimeChange({})
        eventFormService.validate()
      },
    })

    const minTime = computed(() => {
      return endDate.value === startDate.value && startTime.value
        ? startTime.value
        : null
    })

    const validTimeFormat = computed(() => {
      return (v: string) => {
        if (!v) return true
        return (
          /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(v) ||
          "Das gültige Format ist HH:MM"
        )
      }
    })

    const infoIcon = mdiInformationOutline

    return {
      t$,
      eventFormService,
      startDate,
      endDate,
      endDatePickerDate,
      allDay,
      startTime,
      endTime,
      minTime,
      validTimeFormat,
      infoIcon,
    }
  },
})
</script>
